import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { SalesBannerWrapper } from "./styles";
import { useCountdown } from "../../hooks/useCountDown";
import { SaleBannerConfig } from "./dto";

const VISIBLE_PATHS = ["/", "/invitation-templates", "/pricing"];
const CONFIG_REFRESH_INTERVAL_MS = 60000;
const HEIGHT_CSS_VAR = "--sales-banner-height";
const FALLBACK_TARGET_DATE = new Date(0).toISOString();

const pad = (value: number) => String(value).padStart(2, "0");

const SalesBanner = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const isEligibleRoute = VISIBLE_PATHS.includes(location.pathname);
	const [config, setConfig] = useState<SaleBannerConfig | null>(null);
	const rootRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const configUrl = process.env.REACT_APP_SALE_BANNER_CONFIG_URL;
		if (!isEligibleRoute || !configUrl) {
			return;
		}

		let cancelled = false;
		const loadConfig = () => {
			const separator = configUrl.includes("?") ? "&" : "?";
			const cacheBustedUrl = `${configUrl}${separator}v=${Date.now()}`;
			fetch(cacheBustedUrl, { cache: "no-store" })
				.then(response => (response.ok ? response.json() : null))
				.then((data: SaleBannerConfig | null) => {
					if (!cancelled && data) {
						setConfig(data);
					}
				})
				.catch(() => {});
		};

		loadConfig();
		const interval = setInterval(loadConfig, CONFIG_REFRESH_INTERVAL_MS);
		return () => {
			cancelled = true;
			clearInterval(interval);
		};
	}, [isEligibleRoute]);

	const endTimeMs = config ? new Date(config.endTime).getTime() : 0;
	const countdown = useCountdown(config?.endTime || FALLBACK_TARGET_DATE);
	const now = Date.now();
	const isExpired = !!config && now >= endTimeMs;
	const isVisible = isEligibleRoute && !!config && config.enabled && !isExpired;

	useEffect(() => {
		const root = document.documentElement;
		if (!isVisible) {
			root.style.setProperty(HEIGHT_CSS_VAR, "0px");
			return;
		}
		const node = rootRef.current;
		if (!node) {
			return;
		}
		const updateHeight = () => root.style.setProperty(HEIGHT_CSS_VAR, `${node.offsetHeight - 1}px`);
		updateHeight();
		const observer = new ResizeObserver(updateHeight);
		observer.observe(node);
		return () => {
			observer.disconnect();
			root.style.setProperty(HEIGHT_CSS_VAR, "0px");
		};
	}, [isVisible]);

	if (!isVisible || !config) {
		return null;
	}

	const handleCtaClick = () => {
		const href = config.ctaHref;
		if (!href) {
			return;
		}
		try {
			const url = new URL(href, window.location.origin);
			if (url.origin === window.location.origin) {
				navigate(`${url.pathname}${url.search}${url.hash}`);
			} else {
				window.location.assign(url.href);
			}
		} catch {
			navigate(href);
		}
	};

	const startTimeMs = endTimeMs - config.totalDurationMs;
	const progress =
		config.totalDurationMs > 0 ? Math.min(100, Math.max(0, ((now - startTimeMs) / config.totalDurationMs) * 100)) : 0;

	return (
		<SalesBannerWrapper ref={rootRef}>
			<Box className={"progress_track"}>
				<Box className={"progress_fill"} style={{ width: `${progress}%` }} />
			</Box>
			<Box className={"content"}>
				<Box className={"copy"}>
					<Typography component={"span"} className={"discount_label"}>
						{config.discountLabel}
					</Typography>
					{config.subCopy && (
						<Typography component={"span"} className={"sub_copy"}>
							{config.subCopy}
						</Typography>
					)}
				</Box>
				<Box className={"timer"}>
					{countdown.days > 0 && (
						<Box className={"timer_segment"}>
							<span className={"value"}>{pad(countdown.days)}</span>
							<span className={"label"}>d</span>
						</Box>
					)}
					<Box className={"timer_segment"}>
						<span className={"value"}>{pad(countdown.hours)}</span>
						<span className={"label"}>h</span>
					</Box>
					<Box className={"timer_segment"}>
						<span className={"value"}>{pad(countdown.minutes)}</span>
						<span className={"label"}>m</span>
					</Box>
					<Box className={"timer_segment"}>
						<span className={"value"}>{pad(countdown.seconds)}</span>
						<span className={"label"}>s</span>
					</Box>
				</Box>
				{config.ctaLabel && (
					<button type={"button"} className={"cta"} onClick={handleCtaClick}>
						{config.ctaLabel}
					</button>
				)}
			</Box>
		</SalesBannerWrapper>
	);
};

export default SalesBanner;
